import {
    useState,
    useEffect,
    Suspense,
    lazy,
    useRef,
    memo,
    FC,
    ReactNode,
    useCallback,
} from 'react';
import { useFragment, graphql, useRelayEnvironment } from 'react-relay';
import classNames from 'classnames';

import FolderIcon from 'dibs-icons/exports/legacy/Folder';
import HeartIcon from 'dibs-icons/exports/legacy/HeartFilled';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import { hasKey } from 'dibs-ts-utils/exports/hasKey';
import { keys } from 'dibs-ts-utils/exports/keys';

import { HeartWrapperLazy } from '../../../global/HeartWrapperLazy';
import { FolderWrapperLazy } from '../../../global/FolderWrapperLazy';
import {
    locations,
    auctionLocations,
    authLabels,
    folderTrackingHooks,
    trackHeartFavorited,
    trackHeartUnfavorited,
} from '../../../utils/tracking/shared/favoritesTracking';
import { loadAddToFolderModalWrapper } from '../../../global/loadAddToFolderModalWrapper';
import {
    SAVE_ITEM_FLOW,
    SAVE_ITEM_TO_FOLDER_FLOW,
    SAVE_AUCTION_ITEM_FLOW,
    WATCH_AUCTION_ITEM_FLOW,
    SAVE_AUCTION_ITEM_TO_FOLDER_FLOW,
    sharedLoginCheck,
} from '../../../authentication/sharedLoginCheck';
import {
    FORCE_FAVORITE_ITEM_ID,
    FORCE_FOLDER_ITEM_ID,
    setForceFavoriteItemId,
    getForceFavoriteItemId,
    clearForceFavoriteItemId,
    setAuctionPromptDismissedTimestamp,
} from './localStorageHelpers';

import type { FavoritesItem_item$key } from './__generated__/FavoritesItem_item.graphql';
import type { FavoritesProviderQuery$data } from './__generated__/FavoritesProviderQuery.graphql';

import styles from './FavoriteIcons.scss';

const FavoritesTooltipLazy = lazy(
    () => import(/* webpackChunkName: "FavoritesTooltip" */ './FavoritesTooltip')
);

type HeartFlowType =
    | typeof SAVE_ITEM_FLOW
    | typeof WATCH_AUCTION_ITEM_FLOW
    | typeof SAVE_AUCTION_ITEM_FLOW;

type FolderTrackingHooks = {
    trackFolderClick: (location: string) => void;
    trackItemRemoved: (location: string) => void;
    trackItemAdded: (location: string, noteIncluded: boolean, itemPk: string) => void;
    trackFolderCreated: (location: string, noteIncluded: boolean) => void;
};

type Props = {
    itemImage?: string;
    location: keyof typeof locations;
    // passed from FavoritesProvider
    disable?: boolean;
    userId?: string | null;
    userIds: string[];
    itemIds: string[];
    singlePortfolioItem: boolean;
    loadPortfolioItemData: boolean;
    viewer?: FavoritesProviderQuery$data['viewer'] | null;
    fetchHeart: boolean;
    fetchFolder: boolean;
    heartIconClass?: string;
    heartIconWrapperClass?: string;
    folderIconClass?: string;
    folderIconWrapperClass?: string;
    wrapperClass?: string;
    item: FavoritesItem_item$key | null;
    showFolderPlusIcon?: boolean;
    showTooltip?: boolean;
    onFavorited?: () => void;
    onUnFavorited?: () => void;
    onFolderActionStart?: () => void;
    onFolderActionEnd?: () => void;
    animateHeartPulse?: boolean;
    isForceFavoriteItem?: boolean;
    setIsForceFavoriteItem?: (isForceFavoriteItem: boolean) => void;
    onIsFilledChange?: (isFilled: boolean) => void;
    wrapperComponent?: 'div' | 'Link';
};

export const FavoritesItem: FC<Props> = memo(
    ({
        itemImage,
        heartIconClass,
        heartIconWrapperClass,
        folderIconClass,
        folderIconWrapperClass,
        wrapperClass,
        item: itemRef,
        showFolderPlusIcon,
        showTooltip,
        onFavorited,
        onUnFavorited,
        onFolderActionStart,
        onFolderActionEnd,
        animateHeartPulse,
        isForceFavoriteItem,
        setIsForceFavoriteItem,
        onIsFilledChange,
        // passed from FavoritesProvider
        location,
        disable,
        userId,
        userIds,
        itemIds,
        singlePortfolioItem,
        loadPortfolioItemData,
        viewer,
        fetchHeart = true,
        fetchFolder = true,
        wrapperComponent,
    }) => {
        const relayEnvironment = useRelayEnvironment();
        const item = useFragment(
            graphql`
                fragment FavoritesItem_item on Item {
                    serviceId
                    title
                    isAuctionEnabled
                    isSponsored
                }
            `,
            itemRef
        );
        const [openAddToFolderModal, setOpenAddToFolderModal] = useState(false);
        const favoritesRef = useRef(null);
        const { title: itemTitle, isAuctionEnabled, serviceId: itemId, isSponsored } = item || {};

        useEffect(() => {
            const shouldOpenAddToFolderModal =
                getForceFavoriteItemId(FORCE_FOLDER_ITEM_ID) === itemId;

            if (shouldOpenAddToFolderModal) {
                setOpenAddToFolderModal(true);
                clearForceFavoriteItemId(FORCE_FOLDER_ITEM_ID);
            }
        }, [itemId]);

        const onAddItemSuccess = useCallback((): void => {
            if (authModalLoader.isPaused()) {
                authModalLoader.resume();
            }

            if (onFolderActionEnd) {
                onFolderActionEnd();
            }
        }, [onFolderActionEnd]);

        const getTrackingLocation = useCallback((): string => {
            if (!isAuctionEnabled || !auctionLocations[location]) {
                return location;
            }
            return auctionLocations[location];
        }, [isAuctionEnabled, location]);

        const getFolderTrackingHooks = useCallback((): FolderTrackingHooks => {
            let hooks = folderTrackingHooks;
            const trackingLocation = getTrackingLocation();
            if (location) {
                hooks = keys(folderTrackingHooks).reduce((map, current) => {
                    map[current] = folderTrackingHooks[current].bind(null, trackingLocation);
                    return map;
                }, {} as FolderTrackingHooks);
            }

            return hooks;
        }, [getTrackingLocation, location]);

        const showAuthModal = useCallback(
            ({
                flow,
                onSuccess,
                onError,
                shouldPauseAuthFlow,
            }: {
                flow:
                    | typeof SAVE_AUCTION_ITEM_TO_FOLDER_FLOW
                    | typeof SAVE_ITEM_TO_FOLDER_FLOW
                    | typeof SAVE_AUCTION_ITEM_FLOW
                    | typeof SAVE_ITEM_FLOW
                    | typeof WATCH_AUCTION_ITEM_FLOW;
                onSuccess: ({
                    userId,
                    isVerifiedTrade,
                }: {
                    userId: string;
                    isVerifiedTrade: boolean;
                }) => void;
                onError?: (reason?: string) => void;
                shouldPauseAuthFlow?: (state: $TSFixMe) => boolean;
            }): void => {
                sharedLoginCheck({
                    // @ts-ignore sharedLoginCheck still in jsx - is not assignable to parameter of type 'Function'
                    onSuccess,
                    onError,
                    authOptions: {
                        flow,
                        shouldPauseAuthFlow,
                        ga: { label: hasKey(authLabels, location) ? authLabels[location] : '' },
                        additionalProps: {
                            item: { firstPhotoWebPath: itemImage, title: itemTitle },
                        },
                    },
                });
            },
            [itemTitle, itemImage, location]
        );

        const getFolderModalComponent = useCallback((): Promise<void> => {
            const flow = isAuctionEnabled
                ? SAVE_AUCTION_ITEM_TO_FOLDER_FLOW
                : SAVE_ITEM_TO_FOLDER_FLOW;

            return new Promise((resolve, reject) => {
                showAuthModal({
                    flow,
                    shouldPauseAuthFlow: (state: $TSFixMe) => state.step === 0,
                    onSuccess: ({
                        userId: authPayloadUserId,
                        isVerifiedTrade: authPayloadIsVerifiedTrade,
                    }) => {
                        if (authPayloadIsVerifiedTrade) {
                            // after trade user logs in, page is reloaded, so there's no point in showing modal
                            setForceFavoriteItemId(FORCE_FOLDER_ITEM_ID, itemId || '');
                            return resolve();
                        }
                        // @ts-ignore loadAddToFolderModalWrapper still in jsx and expects 0 args
                        return loadAddToFolderModalWrapper({ authPayloadUserId }).then(resolve);
                    },
                    onError: reject,
                });
            });
        }, [isAuctionEnabled, itemId, showAuthModal]);

        const handleOnUnFavorited = useCallback((): void => {
            onUnFavorited?.();
            const trackingLocation = getTrackingLocation();
            trackHeartUnfavorited(trackingLocation, itemId, isSponsored);
        }, [getTrackingLocation, itemId, onUnFavorited, isSponsored]);

        const handleOnFavorited = useCallback((): void => {
            setAuctionPromptDismissedTimestamp();
            onFavorited?.();
            const trackingLocation = getTrackingLocation();
            trackHeartFavorited(trackingLocation, itemId, isSponsored);
        }, [getTrackingLocation, itemId, onFavorited, isSponsored]);

        const renderHeart = useCallback((): ReactNode => {
            if (disable) {
                return <HeartIcon className={styles.icon} />;
            }

            const isPdpWatch = location === locations.AUCTION_PDP_WATCH;

            let flow: HeartFlowType = SAVE_ITEM_FLOW;
            if (isPdpWatch) {
                flow = WATCH_AUCTION_ITEM_FLOW;
            } else if (isAuctionEnabled) {
                flow = SAVE_AUCTION_ITEM_FLOW;
            }

            return (
                <HeartWrapperLazy
                    load
                    itemId={itemId}
                    userId={userId}
                    viewer={viewer}
                    authModalShow={(cb?: () => void) =>
                        showAuthModal({
                            flow,
                            onSuccess: ({ isVerifiedTrade: authPayloadIsVerifiedTrade }) => {
                                if (authPayloadIsVerifiedTrade) {
                                    setForceFavoriteItemId(FORCE_FAVORITE_ITEM_ID, itemId || '');
                                }
                                if (cb && typeof cb === 'function') {
                                    cb();
                                }
                            },
                        })
                    }
                    onFavorited={handleOnFavorited}
                    onUnFavorited={handleOnUnFavorited}
                    className={heartIconClass}
                    buttonClass={styles.heartButton}
                    theme="dark"
                    animateHeartPulse={animateHeartPulse}
                    forceFavoriteItem={isForceFavoriteItem}
                    onIsFilledChange={onIsFilledChange}
                    setIsForceFavoriteItem={setIsForceFavoriteItem}
                    wrapperComponent={wrapperComponent}
                    hideTotalLikesCount={isPdpWatch}
                />
            );
        }, [
            animateHeartPulse,
            disable,
            handleOnFavorited,
            handleOnUnFavorited,
            heartIconClass,
            isForceFavoriteItem,
            isAuctionEnabled,
            itemId,
            onIsFilledChange,
            showAuthModal,
            userId,
            viewer,
            setIsForceFavoriteItem,
            wrapperComponent,
            location,
        ]);

        const renderFolder = useCallback((): ReactNode => {
            if (disable) {
                return <FolderIcon className={styles.icon} />;
            }

            return (
                <FolderWrapperLazy
                    load
                    userId={userId}
                    userIds={userIds}
                    primaryItemPk={itemId}
                    selectedItemIds={itemIds}
                    singlePortfolioItem={singlePortfolioItem}
                    loadPortfolioItemData={loadPortfolioItemData}
                    viewer={viewer}
                    environment={relayEnvironment}
                    trackingHooks={getFolderTrackingHooks()}
                    getModalComponent={getFolderModalComponent}
                    additionalIconClass={folderIconClass}
                    onAddItemSuccess={onAddItemSuccess}
                    shouldFillInFolder={!!userId}
                    openOnRender={openAddToFolderModal}
                    theme="dark"
                    showPlusIcon={showFolderPlusIcon}
                    onFolderSave={onFolderActionStart}
                    onFolderRemove={onFolderActionStart}
                    onNewFolderSave={onFolderActionStart}
                    onRemoveItemSuccess={onFolderActionEnd}
                    onFolderClick={onFolderActionStart}
                    closeModal={onFolderActionEnd}
                />
            );
        }, [
            disable,
            folderIconClass,
            getFolderModalComponent,
            getFolderTrackingHooks,
            itemId,
            itemIds,
            loadPortfolioItemData,
            onAddItemSuccess,
            onFolderActionEnd,
            onFolderActionStart,
            openAddToFolderModal,
            relayEnvironment,
            showFolderPlusIcon,
            singlePortfolioItem,
            userId,
            userIds,
            viewer,
        ]);

        if (disable) {
            return null;
        }

        return (
            <div className={classNames(styles.wrap, wrapperClass)} ref={favoritesRef}>
                {showTooltip && (
                    <Suspense fallback="">
                        <FavoritesTooltipLazy elementRef={favoritesRef} />
                    </Suspense>
                )}
                <Suspense fallback="">
                    {fetchHeart && (
                        <div
                            className={classNames(styles.iconWrap, {
                                [heartIconWrapperClass || '']: !!heartIconWrapperClass,
                            })}
                            data-tn="item-save-to-portfolio"
                        >
                            {renderHeart()}
                        </div>
                    )}
                    {fetchFolder && (
                        <div
                            className={classNames(styles.iconWrap, {
                                [folderIconWrapperClass || '']: !!folderIconWrapperClass,
                            })}
                            data-tn="item-save-to-folder"
                        >
                            {renderFolder()}
                        </div>
                    )}
                </Suspense>
            </div>
        );
    }
);
